import texts from './texts';
import {ChartDataSetItem, IChartConfig, ReportColumn} from '../../types';
import {AnalysisType} from '../../services/analysisService';
import {SingleSeriesChartTypesOptions, MultiSeriesChartTypesOptions} from '../../chartConsts';
import {getLang} from '../../../../Localization';

export const getAnalysisOptionLabelByType = (type: AnalysisType): string => {
    switch (type) {
        case AnalysisType.EXECUTIVE:
            return texts.executive;
        case AnalysisType.COMPARATIVE:
            return texts.comparative;
        case AnalysisType.STATISTICAL:
            return texts.statistical;
        default:
            return '';
    }
};

const getColumnTitles = (column: ReportColumn): string => {
    const {Title, OrigTitle, IsExpression} = column;
    if (IsExpression) {
        return Title || OrigTitle;
    } else {
        return OrigTitle;
    }
};

export const getAxesTitles = (chartConfig: IChartConfig) => {
    const {xAxis, yAxis, bubbleConfig} = chartConfig;
    const xAxisName = getColumnTitles(xAxis);
    const yAxisName = getColumnTitles(yAxis);
    const axes = {xAxisName, yAxisName};
    if (bubbleConfig) {
        const {zBubbleSize, category} = bubbleConfig;
        axes['zAxisName'] = getColumnTitles(zBubbleSize);
        axes['bubbleCategory'] = getColumnTitles(category);
    }
    return axes;
};

const genBasicChartDataSet = (jsonData: any): ChartDataSetItem[] => {
    const data = jsonData?.data?.data;
    if (!data) {
        console.warn('genBasicChartDataSet - No data found in jsonData');
        return null;
    }
    const values = data.map(item => {
        return {
            x: item.label,
            y: item.value
        };
    });
    const res = {
        name: null,
        data: values
    };
    return [res];
};

const genBubbleChartDataSet = (jsonData: any): ChartDataSetItem[] => {
    const data = jsonData?.data?.dataset?.[0]?.data;
    if (!data) {
        console.warn('genBubbleChartDataSet - No data found in jsonData');
        return null;
    }
    const res = data.map(item => {
        const {x, y, z, name, originalcategoryvalue} = item;
        const bubble = {
            name: name,
            x,
            y,
            z
        };
        if (name !== originalcategoryvalue) {
            bubble['origName'] = originalcategoryvalue;
        }
        return bubble;
    });
    return res;
};

const genMultiSeriesChartDataSet = (jsonData: any): ChartDataSetItem[] => {
    const data = jsonData?.data;
    if (!data) {
        console.warn('genMultiSeriesChartDataSet - No data found in jsonData');
        return null;
    }
    const categories = data.categories?.[0]?.category || [];
    const dataset = data.dataset || [];
    const res = dataset.map(serie => {
        return {
            name: serie.seriesname,
            data: serie.data.map((item, index) => ({
                x: categories[index]?.label || `Category ${index + 1}`,
                y: item.value
            }))
        };
    });
    return res;
};

export const getDataSetValueByType = (type: string, data: any): ChartDataSetItem[] => {
    switch (type) {
        case SingleSeriesChartTypesOptions.bubble:
            return genBubbleChartDataSet(data);
        case MultiSeriesChartTypesOptions.msbar2d:
        case MultiSeriesChartTypesOptions.mscolumn2d:
        case MultiSeriesChartTypesOptions.stackedbar2d:
        case MultiSeriesChartTypesOptions.stackedcolumn2d:
        case MultiSeriesChartTypesOptions.msline:
            return genMultiSeriesChartDataSet(data);
        default:
            return genBasicChartDataSet(data);
    }
};

const getDateFormatById = (dateFormatId: number): string => {
    const isAmericanEnglish = getLang() === '3';
    const defaultFormat = isAmericanEnglish ? 'MM/DD/YY' : 'DD/MM/YY';
    switch (Number(dateFormatId)) {
        case 0:
            return defaultFormat;
        case 11:
            return 'DD/MM/YY';
        case 12:
            return 'DD/MM/YYYY';
        case 13:
            return 'MM/DD/YY';
        case 14:
            return 'MM/DD/YYYY';
        case 15:
            return 'MMM DD, YYYY';
        case 16:
            return 'mmm DD, YYYY';
        case 17:
            return 'DD-MMM-YYYY';
        case 18:
            return 'DD-mmm-YYYY';
        case 19:
            return 'YYYY-MMM-DD';
        case 21:
            return 'DD MM YYYY';
        case 22:
            return 'DD mmm YY';
        case 23:
            return 'DD MMM YYYY';
        case 24:
            return 'YYYY, MMM';
        case 25:
            return 'YYYY';
        case 26:
            return 'DD MMM YY';
        case 28:
            return 'DD mmmm YY';
        case 31:
            return 'DD MMMM YY';
        case 32:
            return 'DD-MM-YYYY hh:mm';
        case 33:
            return 'DD-MM-YY hh:mm';
        case 34:
            return 'YYYYWW';
        case 35:
            return 'YY-MMM';
        case 36:
            return 'YYYY/QQ';
        default:
            return defaultFormat;
    }
};

export const getDateFormat = (chartConfig: IChartConfig) => {
    const {xAxis, yAxis} = chartConfig;
    const dateFormatId = xAxis?.DateFormat || yAxis?.DateFormat;
    if (Number(dateFormatId) >= 0) {
        const dateFormat = getDateFormatById(Number(dateFormatId));
        return {dateFormat};
    }
    return {};
};
